import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Workspace } from '../types';
import { listenToUserWorkspaces } from '../repositories/workspaceApiRepository';
import { useAuth } from './AuthContext';
import { createWorkspace, renameWorkspace } from '../api/workspaceApi';
import axios from 'axios';

interface WorkspaceContextType {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  workspacesReady: boolean;
  isProvisioningWorkspace: boolean;
  profileSetupWorkspaceId: string | null;
  profileSetupWorkspace: Workspace | null;
  profileSetupWorkspacePending: boolean;
  profileSetupError: string | null;
  profileSetupSubmitting: boolean;
  setCurrentWorkspace: (ws: Workspace | null) => void;
  handleSetWorkspace: (ws: Workspace) => void;
  setProfileSetupWorkspaceId: (id: string | null) => void;
  setProfileSetupWorkspace: (ws: Workspace | null) => void;
  setProfileSetupError: (error: string | null) => void;
  setProfileSetupSubmitting: (submitting: boolean) => void;
  setWorkspaces: React.Dispatch<React.SetStateAction<Workspace[]>>;
  setIsProvisioningWorkspace: (val: boolean) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { currentUser, backendUser, persistUsers, setCurrentUser } = useAuth();
  
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspacesReady, setWorkspacesReady] = useState(false);
  const [isProvisioningWorkspace, setIsProvisioningWorkspace] = useState(false);
  const workspaceListenerRef = useRef<(() => void) | null>(null);
  
  // Profile Setup State
  const [profileSetupWorkspaceId, setProfileSetupWorkspaceId] = useState<string | null>(null);
  const [profileSetupWorkspacePending, setProfileSetupWorkspacePending] = useState(false);
  const [profileSetupWorkspace, setProfileSetupWorkspace] = useState<Workspace | null>(null);
  const [profileSetupError, setProfileSetupError] = useState<string | null>(null);
  const [profileSetupSubmitting, setProfileSetupSubmitting] = useState(false);

  // Initialize selected workspace from local storage or first available
  useEffect(() => {
    if (!currentUser) {
      setCurrentWorkspace(null);
      return;
    }

    if (!currentWorkspace) {
      const cacheKey = `amplifyChatCurrentWorkspaceData:${currentUser.id}`;
      const cachedWorkspaceRaw = localStorage.getItem(cacheKey);
      if (cachedWorkspaceRaw) {
        try {
          const cachedWorkspace = JSON.parse(cachedWorkspaceRaw) as Workspace;
          if (cachedWorkspace?.id) {
            setCurrentWorkspace(cachedWorkspace);
          }
        } catch {
          localStorage.removeItem(cacheKey);
        }
      }
    }

    const attemptWorkspaceLoad = (workspaceId: string | null) => {
      if (!workspaceId) return false;
      if (!currentUser.workspaces.includes(workspaceId)) return false;
      const workspaceToLoad = workspaces.find(ws => ws.id === workspaceId);
      if (workspaceToLoad) {
        if (currentWorkspace?.id !== workspaceToLoad.id) {
          handleSetWorkspace(workspaceToLoad);
        }
        return true;
      }
      return false;
    };

    const lastWorkspaceId = localStorage.getItem('amplifyChatCurrentWorkspace');
    if (attemptWorkspaceLoad(lastWorkspaceId)) return;
    
    if (currentUser.workspaces.length > 0) {
      attemptWorkspaceLoad(currentUser.workspaces[0]);
      return;
    }

    setCurrentWorkspace(null);
  }, [currentUser, workspaces]); // Note: currentWorkspace & handleSetWorkspace omitted to prevent loops in this specific effect

  const handleSetWorkspace = useCallback((workspace: Workspace) => {
    setCurrentWorkspace(prev => (prev?.id === workspace.id ? { ...prev, ...workspace } : workspace));
    localStorage.setItem('amplifyChatCurrentWorkspace', workspace.id);
    if (currentUser) {
      const cacheKey = `amplifyChatCurrentWorkspaceData:${currentUser.id}`;
      localStorage.setItem(cacheKey, JSON.stringify(workspace));
    }
  }, [currentUser]);

  // Listen to workspaces
  useEffect(() => {
    if (!currentUser || !backendUser) {
      if (workspaceListenerRef.current) {
        workspaceListenerRef.current();
        workspaceListenerRef.current = null;
      }
      setWorkspaces([]);
      setWorkspacesReady(false);
      setIsProvisioningWorkspace(false);
      return;
    }

    let isActive = true;
    const registerWorkspaceListener = async () => {
      try {
        const unsub = await listenToUserWorkspaces(currentUser.id, {
          onUpdate: workspaceList => {
            if (!isActive) return;
            setWorkspaces(workspaceList);
            setWorkspacesReady(true);
          },
          onError: err => {
            if (!isActive) return;
            console.error('Workspace listener error:', err);
            setWorkspacesReady(true);
          }
        });
        if (isActive) {
          workspaceListenerRef.current = unsub;
        } else {
          unsub();
        }
      } catch (err) {
        if (!isActive) return;
        console.error('Failed to register workspace listener:', err);
        setWorkspacesReady(true);
      }
    };
    
    void registerWorkspaceListener();
    return () => {
      isActive = false;
      if (workspaceListenerRef.current) {
        workspaceListenerRef.current();
        workspaceListenerRef.current = null;
      }
    };
  }, [currentUser, backendUser]);

  // Profile setup workspace creation
  const profileSetupActive = !!backendUser && backendUser.profile_complete === false;
  useEffect(() => {
    if (!profileSetupActive) {
      setProfileSetupWorkspaceId(null);
      setProfileSetupWorkspacePending(false);
      setProfileSetupError(null);
      setProfileSetupWorkspace(null);
      return;
    }
    
    if (workspaces.length > 0) {
      if (!profileSetupWorkspaceId) {
        setProfileSetupWorkspaceId(workspaces[0].id);
        setProfileSetupWorkspace(workspaces[0]);
      }
      return;
    }
    if (profileSetupWorkspacePending || profileSetupWorkspaceId) return;
    if (!currentUser) return;

    const placeholderName = `Brand-${Math.random().toString(36).slice(2, 7)}`;
    setProfileSetupWorkspacePending(true);
    createWorkspace({ name: placeholderName, tier: 'free' })
      .then(workspace => {
        setProfileSetupWorkspaceId(workspace.id);
        setProfileSetupWorkspace(workspace);
        const updatedUser = {
          ...currentUser,
          workspaces: Array.from(new Set([...currentUser.workspaces, workspace.id])),
        };
        persistUsers(prev => prev.map(u => (u.id === currentUser.id ? updatedUser : u)));
        setCurrentUser(updatedUser);
      })
      .catch(error => {
        console.error('Failed to create brand in background:', error);
        setProfileSetupError('Unable to prepare your brand. Please try again.');
      })
      .finally(() => {
        setProfileSetupWorkspacePending(false);
      });
  }, [profileSetupActive, workspaces, profileSetupWorkspaceId, profileSetupWorkspacePending, currentUser, persistUsers, setCurrentUser]);

  const value = {
    workspaces,
    currentWorkspace,
    workspacesReady,
    isProvisioningWorkspace,
    profileSetupWorkspaceId,
    profileSetupWorkspace,
    profileSetupWorkspacePending,
    profileSetupError,
    profileSetupSubmitting,
    setCurrentWorkspace,
    handleSetWorkspace,
    setProfileSetupWorkspaceId,
    setProfileSetupWorkspace,
    setProfileSetupError,
    setProfileSetupSubmitting,
    setWorkspaces,
    setIsProvisioningWorkspace,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
